import { useQuery } from '@tanstack/react-query'
import { Badge, Box, Button, Card, Checkbox, Flex, Heading, IconButton, Select, Text, TextField, Tooltip } from '@radix-ui/themes'
import { PlusIcon, TrashIcon } from '@radix-ui/react-icons'
import type { UseFormRegister, UseFormSetValue } from 'react-hook-form'
import cn from 'classnames'
import { FieldLabel } from '~/components/FieldLabel'
import { useTRPC } from '~/utils/trpc'
import { plural } from '~/utils/plural'
import type { BatchDraft, SiteDraft, StationDraft } from '../batchDraft'
import { SITE_STATIONS_STALE_MS, STATION_TYPE_OPTIONS, type StationType } from '../constants'
import type { SiteStation } from '../csv'
import { ClientPhotoCheckbox } from './ClientPhotoCheckbox'

const NEW_STATION = '__new_station__'

interface StationFieldsProps {
  station: StationDraft
  index: number
  ordinal: number
  canAdopt: boolean
  options: SiteStation[]
  loadingOptions: boolean
  compact: boolean
  removable: boolean
  disabled: boolean
  register: UseFormRegister<BatchDraft>
  setValue: UseFormSetValue<BatchDraft>
  onRemove: (index: number) => void
}

const StationFields = ({
  station,
  index,
  ordinal,
  canAdopt,
  options,
  loadingOptions,
  compact,
  removable,
  disabled,
  register,
  setValue,
  onRemove
}: StationFieldsProps) => {
  const isAdopting = station.mode === 'adopt'
  const size = compact ? '1' : '2'
  const removeButton = removable ? (
    <Tooltip content="Remove station">
      <IconButton variant="soft" color="gray" size="1" disabled={disabled} onClick={() => onRemove(index)}>
        <TrashIcon />
      </IconButton>
    </Tooltip>
  ) : null

  return (
    <Box className={cn('border-t border-t-[color:var(--gray-6)]', compact ? 'pt-2' : 'pt-3')}>
      {compact ? null : (
        <Flex justify="between" align="center" mb="2">
          <Text size="2" weight="medium">
            Station {ordinal}
          </Text>
          {removeButton}
        </Flex>
      )}

      <Flex gap={compact ? '2' : '3'} wrap="wrap" align="end">
        {/* A site being created holds nothing to adopt, so the picker is only worth showing for one that exists. */}
        {canAdopt ? (
          <Box className={compact ? 'grow basis-48' : 'basis-full'} maxWidth={compact ? undefined : '360px'}>
            <FieldLabel hidden={compact}>Station</FieldLabel>
            <Select.Root
              size={size}
              value={isAdopting ? station.waterStationId || undefined : NEW_STATION}
              disabled={disabled || loadingOptions}
              onValueChange={(v) => {
                setValue(`stations.${index}.mode`, v === NEW_STATION ? 'create' : 'adopt')
                setValue(`stations.${index}.waterStationId`, v === NEW_STATION ? '' : v)
              }}
            >
              <Select.Trigger className="w-full" placeholder={loadingOptions ? 'Loading…' : 'Select a station'} />
              <Select.Content>
                <Select.Item value={NEW_STATION}>+ Create new station…</Select.Item>
                {options.length > 0 ? <Select.Separator /> : null}
                {options.map((s) => (
                  <Select.Item key={s.id} value={s.id}>
                    {s.name}
                    {s.internal_id ? ` (${s.internal_id})` : ''}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Box>
        ) : null}

        {isAdopting ? null : (
          <>
            <Box className={compact ? 'grow basis-48' : 'grow basis-64'}>
              <FieldLabel hidden={compact}>Station name</FieldLabel>
              <TextField.Root
                size={size}
                placeholder={compact ? 'Station name' : 'e.g. Main meter'}
                disabled={disabled}
                {...register(`stations.${index}.stationName`)}
              />
            </Box>
            <Box className={compact ? 'basis-28' : 'grow basis-48'}>
              <FieldLabel hidden={compact}>Station type</FieldLabel>
              <Select.Root
                size={size}
                value={station.stationType || undefined}
                disabled={disabled}
                onValueChange={(v) => setValue(`stations.${index}.stationType`, v as StationType)}
              >
                <Select.Trigger className="w-full" placeholder={compact ? 'Type' : 'Select a type'} />
                <Select.Content>
                  {STATION_TYPE_OPTIONS.map((o) => (
                    <Select.Item key={o} value={o}>
                      {o}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>
            <Box className={compact ? 'basis-28' : 'grow basis-40'}>
              <FieldLabel hidden={compact}>Internal ID</FieldLabel>
              <TextField.Root
                size={size}
                placeholder={compact ? 'Internal ID' : 'Optional'}
                disabled={disabled}
                {...register(`stations.${index}.internalId`)}
              />
            </Box>
          </>
        )}

        {compact ? (
          <>
            <Tooltip content="Client sends the meter photo">
              <Text as="label" size="1" className="flex items-center gap-1 h-6">
                <Checkbox
                  size="1"
                  checked={station.pictureFromClientRequired}
                  disabled={disabled}
                  onCheckedChange={(checked) => setValue(`stations.${index}.pictureFromClientRequired`, checked === true)}
                />
                Photo
              </Text>
            </Tooltip>
            {removeButton}
          </>
        ) : null}
      </Flex>

      {compact ? null : (
        <ClientPhotoCheckbox
          className="mt-3"
          checked={station.pictureFromClientRequired}
          disabled={disabled}
          onChange={(checked) => setValue(`stations.${index}.pictureFromClientRequired`, checked)}
        />
      )}
    </Box>
  )
}

export interface StationEntry {
  station: StationDraft
  index: number
  fieldId: string
}

interface SiteStationsSectionProps {
  site: SiteDraft
  entries: StationEntry[]
  /** Which station of the batch has adopted each existing station, by form index. */
  adoptedBy: Map<string, number>
  compact: boolean
  canAdd: boolean
  disabled: boolean
  register: UseFormRegister<BatchDraft>
  setValue: UseFormSetValue<BatchDraft>
  onAdd: () => void
  onRemove: (index: number) => void
}

export const SiteStationsSection = ({
  site,
  entries,
  adoptedBy,
  compact,
  canAdd,
  disabled,
  register,
  setValue,
  onAdd,
  onRemove
}: SiteStationsSectionProps) => {
  const trpc = useTRPC()

  // A site being created holds nothing to adopt, so the query is only worth running for a real one.
  const stationsQuery = useQuery({
    ...trpc.ops.stationInstallations.listSiteStations.queryOptions({ siteId: site.siteId ?? '' }),
    enabled: !!site.siteId,
    staleTime: SITE_STATIONS_STALE_MS
  })
  const siteStations = stationsQuery.data ?? []

  // A station may be adopted once per batch, so ones chosen elsewhere drop out of the options.
  const availableStations = (index: number) =>
    siteStations.filter((s) => s.already_linked_to === null && (adoptedBy.get(s.id) ?? index) === index)

  return (
    <Card>
      <Flex direction="column" gap={compact ? '2' : '3'}>
        <Flex justify="between" align="center">
          <Flex align="center" gap="2">
            <Heading size="3">{site.siteName.trim() || 'Unnamed site'}</Heading>
            {compact ? (
              <Text size="1" color="gray">
                {plural(entries.length, 'station')}
              </Text>
            ) : null}
          </Flex>
          {site.siteId ? null : (
            <Badge color="amber" variant="soft">
              new site
            </Badge>
          )}
        </Flex>

        {entries.map(({ station, index, fieldId }, ordinal) => (
          <StationFields
            key={fieldId}
            station={station}
            index={index}
            ordinal={ordinal + 1}
            canAdopt={site.siteId !== null}
            options={availableStations(index)}
            loadingOptions={stationsQuery.isLoading}
            compact={compact}
            removable={entries.length > 1}
            disabled={disabled}
            register={register}
            setValue={setValue}
            onRemove={onRemove}
          />
        ))}

        <Box>
          <Button variant="soft" color="gray" size={compact ? '1' : '2'} disabled={disabled || !canAdd} onClick={onAdd}>
            <PlusIcon />
            Add another station
          </Button>
        </Box>
      </Flex>
    </Card>
  )
}
